import React, { useEffect, useState } from 'react';
import { ordersPubApi } from '../api';
import type { ClientOrderItem, PaymentRecord, TimelineEvent } from '../types';

interface Props {
  order: ClientOrderItem;
  onClose: () => void;
  onUpdateOrder: (id: string, payload: Partial<ClientOrderItem>) => Promise<void>;
  onAddPayment: (
    orderId: string,
    payload: { amount: number; payment_date: string; method?: string; note?: string },
  ) => Promise<void>;
  onOpenDiscussion: (order: ClientOrderItem) => void;
  /** When true, render as in-overlay detail page (template b7 orderDetail). */
  embedded?: boolean;
}

/** Order card detail — template Block7 orderDetail, wired to backend. */
export const ClientOrderDetailModal: React.FC<Props> = ({
  order,
  onClose,
  onUpdateOrder,
  onAddPayment,
  onOpenDiscussion,
  embedded = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(order.status);
  const [currentStage, setCurrentStage] = useState(order.stage);
  const [editJournal, setEditJournal] = useState(order.journal);
  const [editTotal, setEditTotal] = useState(String(order.total_amount || 0));
  const [editPaid, setEditPaid] = useState(String(order.paid_amount || 0));
  const [editPayment, setEditPayment] = useState(order.payment_state);
  const [editPublication, setEditPublication] = useState(order.target_date || '');
  const [editNote, setEditNote] = useState(order.internal_note || '');
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>(order.payments || []);
  const [newPayAmount, setNewPayAmount] = useState('');
  const [newPayNote, setNewPayNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setCurrentStatus(order.status);
    setCurrentStage(order.stage);
    setEditJournal(order.journal);
    setEditTotal(String(order.total_amount || 0));
    setEditPaid(String(order.paid_amount || 0));
    setEditPayment(order.payment_state);
    setEditPublication(order.target_date || '');
    setEditNote(order.internal_note || '');
    setPayments(order.payments || []);
  }, [order]);

  useEffect(() => {
    ordersPubApi
      .getTimeline('ORDER', order.id)
      .then(setTimeline)
      .catch(() => setTimeline([]));
  }, [order.id]);

  const authorsLabel = (() => {
    if (!order.authors_json) return order.client_name || '—';
    try {
      const parsed = JSON.parse(order.authors_json);
      return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
    } catch {
      return order.authors_json;
    }
  })();

  const money = (val: number) => `${Number(val || 0).toLocaleString('ru-RU')} ${order.currency || '₸'}`;

  const reloadTimeline = async () => {
    try {
      setTimeline(await ordersPubApi.getTimeline('ORDER', order.id));
    } catch {
      /* keep prior */
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdateOrder(order.id, {
        status: currentStatus,
        stage: currentStage,
        journal: editJournal,
        total_amount: Number(editTotal) || 0,
        paid_amount: Number(editPaid) || 0,
        payment_state: editPayment,
        target_date: editPublication || undefined,
        internal_note: editNote || undefined,
      });
      setEditing(false);
      await reloadTimeline();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amt = parseInt(newPayAmount, 10);
    if (!amt || amt <= 0) return;
    setIsSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await onAddPayment(order.id, {
        amount: amt,
        payment_date: today,
        method: 'Банковский перевод',
        note: newPayNote || undefined,
      });
      setNewPayAmount('');
      setNewPayNote('');
      const updated = await ordersPubApi.getOrderDetail(order.id);
      setPayments(updated.payments || []);
      await reloadTimeline();
    } finally {
      setIsSaving(false);
    }
  };

  const folderLabel = (() => {
    const ref = order.folder_ref || '';
    if (ref.startsWith('file-hub://')) {
      const fileId = ref.replace('file-hub://', '');
      const linked = order.linked_files?.find((f) => f.file_id === fileId);
      return linked?.display_name ? `${linked.display_name} (${fileId})` : fileId;
    }
    return ref || '—';
  })();

  const linkedFilesLabel =
    order.linked_files?.length
      ? order.linked_files
          .map((f) => {
            const title = f.display_name || f.file_id;
            const ver = f.version_no ? ` · ${f.version_no}` : '';
            const role = f.role ? ` (${f.role})` : '';
            return `${title}${ver}${role}`;
          })
          .join('\n')
      : '—';

  const fields: Array<[string, string, string?]> = [
    ['Текущий статус', order.status],
    ['Текущий этап', order.stage],
    ['База / категория', `${order.database}${order.category ? ` · ${order.category}` : ''}`],
    ['Квартиль', order.quartile || '—'],
    ['Процентиль', order.percentile || '—'],
    ['Журнал / направление', `${order.journal}${order.category ? ` / ${order.category}` : ''}`],
    ['Авторы', authorsLabel, 'wide'],
    ['Библиография', order.bibliography || '—', 'full'],
    ['Дата публикации', order.target_date || '—'],
    ['Общая сумма', money(order.total_amount)],
    ['Оплачено', money(order.paid_amount)],
    ['Остаток', money(order.balance)],
    ['Статус оплаты', order.payment_state],
    ['Папка / source', folderLabel],
    ['Файлы File Hub', linkedFilesLabel, 'full'],
    ['Примечание', order.internal_note || '—', 'full'],
  ];

  const detailBody = editing ? (
    <>
      <div className="biz-ops-crumb">
        Заказ №{order.order_number} / <b>Редактирование</b>
      </div>
      <section className="biz-ops-panel">
        <h3>{order.article_title}</h3>
        <div className="biz-ops-edit">
          <label>
            Статус
            <select
              className="biz-ops-select"
              value={currentStatus}
              onChange={(e) => setCurrentStatus(e.target.value)}
            >
              {['В работе', 'Принято', 'Опубликовано', 'Приостановлена', 'Поиск соавторов', 'Ожидает публикации', 'Отклонено'].map(
                (v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ),
              )}
            </select>
          </label>
          <label>
            Этап
            <input
              className="biz-ops-input"
              value={currentStage}
              onChange={(e) => setCurrentStage(e.target.value)}
            />
          </label>
          <label className="full">
            Журнал / площадка
            <input
              className="biz-ops-input"
              value={editJournal}
              onChange={(e) => setEditJournal(e.target.value)}
            />
          </label>
          <label>
            Общая сумма
            <input
              className="biz-ops-input"
              type="number"
              value={editTotal}
              onChange={(e) => setEditTotal(e.target.value)}
            />
          </label>
          <label>
            Оплачено
            <input
              className="biz-ops-input"
              type="number"
              value={editPaid}
              onChange={(e) => setEditPaid(e.target.value)}
            />
          </label>
          <label>
            Статус оплаты
            <select
              className="biz-ops-select"
              value={editPayment}
              onChange={(e) => setEditPayment(e.target.value)}
            >
              {['Оплачено', 'Частично оплачено', 'Не оплачено'].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label>
            Дата публикации
            <input
              className="biz-ops-input"
              value={editPublication}
              onChange={(e) => setEditPublication(e.target.value)}
            />
          </label>
          <label className="full">
            Примечание
            <textarea
              className="biz-ops-textarea"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
            />
          </label>
        </div>
        <div className="biz-ops-toolbar" style={{ marginTop: 12 }}>
          <span className="biz-ops-notice">
            Сохранение создаёт запись в хронологии. Владелец и исполнитель читают одну общую карточку.
          </span>
          <span className="biz-ops-spacer" />
          <button type="button" className="biz-ops-btn" onClick={() => setEditing(false)}>
            Отмена
          </button>
          <button type="button" className="biz-ops-btn mint" disabled={isSaving} onClick={() => void handleSave()}>
            Сохранить
          </button>
        </div>
      </section>
    </>
  ) : (
    <>
      <div className="biz-ops-crumb">
        Заказы клиентов / <b>№ {order.order_number}</b>
      </div>
      <div className="biz-ops-detail-grid">
        <section className="biz-ops-panel">
          <div className="biz-ops-toolbar" style={{ marginTop: 0 }}>
            <div>
              <h3>{order.article_title}</h3>
              <p>{order.journal}</p>
            </div>
            <span className="biz-ops-spacer" />
            <button type="button" className="biz-ops-btn" onClick={() => onOpenDiscussion(order)}>
              Диалог
            </button>
            <button type="button" className="biz-ops-btn gold" onClick={() => setEditing(true)}>
              Редактировать
            </button>
          </div>
          <div className="biz-ops-fields">
            {fields.map(([label, value, span]) => (
              <div key={label} className={`biz-ops-field${span ? ` ${span}` : ''}`}>
                <b>{label}</b>
                <span>{value}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 14 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>Платежи</h3>
            {payments.map((p) => (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  padding: '7px 0',
                  borderBottom: '1px solid rgba(255,255,255,.06)',
                  fontSize: 11,
                }}
              >
                <span>
                  <strong>{p.payment_number}</strong> · {p.payment_date} ({p.method})
                </span>
                <span style={{ color: '#49ddb7', fontWeight: 800 }}>
                  +{Number(p.amount).toLocaleString('ru-RU')} {p.currency}
                </span>
              </div>
            ))}
            <form
              onSubmit={handleAddPaymentSubmit}
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}
            >
              <input
                type="number"
                className="biz-ops-input"
                style={{ width: 140 }}
                placeholder="Сумма"
                value={newPayAmount}
                onChange={(e) => setNewPayAmount(e.target.value)}
              />
              <input
                type="text"
                className="biz-ops-input"
                style={{ flex: 1, minWidth: 160 }}
                placeholder="Примечание к платежу…"
                value={newPayNote}
                onChange={(e) => setNewPayNote(e.target.value)}
              />
              <button type="submit" className="biz-ops-btn mint" disabled={isSaving || !newPayAmount}>
                + Внести платёж
              </button>
            </form>
          </div>
        </section>

        <aside className="biz-ops-panel">
          <h3>Хронология заказа</h3>
          <p style={{ marginBottom: 10 }}>
            Изменения статуса, журнала, оплаты и ответов сохраняются в хронологии.
          </p>
          <div className="biz-ops-timeline">
            {[...timeline].reverse().map((event) => (
              <div key={event.id} className="biz-ops-time">
                <strong>{event.summary}</strong>
                <small>
                  {new Date(event.event_at).toLocaleString('ru-RU')} · {event.actor_name}
                  {event.actor_role ? ` · ${event.actor_role}` : ''}
                </small>
                {event.details ? <small>{event.details}</small> : null}
              </div>
            ))}
            {!timeline.length ? (
              <div className="biz-ops-time">
                <strong>Событий пока нет</strong>
                <small>Изменения появятся после обновления статуса или платежа</small>
              </div>
            ) : null}
          </div>
        </aside>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="biz-ops-wrap" data-order-detail={order.id}>
        {detailBody}
        <div className="biz-ops-toolbar" style={{ marginTop: 14 }}>
          <button type="button" className="biz-ops-btn" onClick={onClose}>
            ← К реестру
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-pub-modal-backdrop biz-ops-detail-backdrop" onClick={onClose}>
      <div className="biz-ops-wrap" style={{ maxWidth: 1200 }} onClick={(e) => e.stopPropagation()}>
        {detailBody}
      </div>
    </div>
  );
};
